import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Link, Typography } from '@material-ui/core';

import DescriptionIcon from '@material-ui/icons/Description';
import ViewHeadlineIcon from '@material-ui/icons/ViewHeadline';
import BusinessIcon from '@material-ui/icons/Business';
import PersonIcon from '@material-ui/icons/Person';
import LaunchIcon from '@material-ui/icons/Launch';
import PinDropIcon from '@material-ui/icons/PinDrop';

import chipStyles from '../../../../../Config/chip';
import Description from '../../../../Common/PlanningApplication/Description';
import Agent from '../../../../Common/PlanningApplication/Agent';
import Applicant from '../../../../Common/PlanningApplication/Applicant';

const SEE_SOURCE = 'See source';

class PlanningCard extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        application: PropTypes.object.isRequired,
        onMouseEnter: PropTypes.func,
        onMouseLeave: PropTypes.func,
        onViewOnMap: PropTypes.func
    };

    static defaultProps = {
        onMouseEnter: null,
        onMouseLeave: null,
        onViewOnMap: null
    };

    constructor (props) {
        super(props);

        this.state = {
            isExpanded: false
        };

        this.getStatusClass = this.getStatusClass.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleOnMouseEnter = this.handleOnMouseEnter.bind(this);
        this.handleOnMouseLeave = this.handleOnMouseLeave.bind(this);
        this.handleViewOnMap = this.handleViewOnMap.bind(this);
    }

    handleChange (event, expanded) {
        this.setState({
            isExpanded: expanded
        });
    }

    optional = (name, company, address) => {
        if ((name && name !== SEE_SOURCE) || company || address) {
            return (
                <>
                    {name && <Typography variant="body2">
                        {name}
                    </Typography>}
                    {company && <Typography variant="body2">
                        {company}
                    </Typography>}
                    {address && <Typography variant="body2">
                        {address}
                    </Typography>}
                </>
            );
        } else {
            return (
                <Typography variant="body2">
                    { name ? 'Data only available at source' : 'No data available'}
                </Typography>
            );
        }
    };

    getStatusClass () {
        const { classes, application } = this.props;
        switch (application.appState) {
            case 'Permitted':
            case 'Conditions':
                return classes.granted;
            case 'Rejected':
                return classes.refused;
            case 'Withdrawn':
                return classes.withdrawn;
            case 'Undecided':
                return classes.pending;
            default:
                return classes.unknown;
        }
    }

    handleOnMouseEnter () {
        const { application, onMouseEnter } = this.props;
        if (onMouseEnter) { onMouseEnter(application.name); }
    }

    handleOnMouseLeave () {
        const { application, onMouseLeave } = this.props;
        if (onMouseLeave) { onMouseLeave(application.name); }
    }

    handleViewOnMap () {
        const { application, onViewOnMap } = this.props;
        if (onViewOnMap) { onViewOnMap(application.point.coordinates); }
    }

    render () {
        const { classes, application } = this.props;
        const { isExpanded } = this.state;

        return (
            <Accordion
                classes={{
                    root: classes.root,
                    expanded: classes.expanded
                }}
                TransitionProps={{ unmountOnExit: true }}
                expanded={isExpanded}
                onChange={this.handleChange}
                onMouseEnter={this.handleOnMouseEnter}
                onMouseLeave={this.handleOnMouseLeave}
            >
                <AccordionSummary classes={{
                    root: classes.summaryRoot,
                    content: classes.summaryContent
                }}>
                    <DescriptionIcon />
                    <div className={classes.summaryContentDiv}>
                        <Typography variant="subtitle1">
                            {application.appType}
                        </Typography>
                        <Typography variant="body2">
                            {application.uid}
                        </Typography>
                    </div>
                    <div className={classes.summaryContentDiv}>
                        <Typography variant="body2">
                            {(new Date(application.startDate)).toLocaleDateString('en-GB')}
                        </Typography>
                        <Typography component="p" className={classnames(classes.chip, this.getStatusClass(application))} variant="caption">
                            {application.status || 'Unknown'}
                        </Typography>
                    </div>
                </AccordionSummary>
                <AccordionDetails classes={{ root: classes.detailsRoot }}>
                    <Grid container>
                        <Grid item xs={12} className={classes.gridLite}>
                            <Description
                                description={application.description}
                                url={application.url || application.sourceUrl}
                            />
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12} className={classes.grid}>
                            <div className={classes.heading}>
                                <Typography className={classes.textWithIcon} variant="subtitle1">
                                    <PinDropIcon />&nbsp;Address
                                </Typography>
                                <Link
                                    // href="#"
                                    component="button"
                                    onClick={this.handleViewOnMap}
                                    // target="_blank"
                                    underline="none"
                                    variant="caption"
                                >
                                    View
                                </Link>
                            </div>
                            <Typography variant="body2">
                                {application.address}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item xs={6} className={classes.gridLite}>
                            <Agent
                                name={application.agentName}
                                company={application.agentCompany}
                                address={application.agentAddress}
                                url={application.url || application.sourceUrl}
                            />
                        </Grid>
                        <Grid item xs={6} className={classes.gridLite}>
                            <Applicant
                                name={application.applicantName}
                                company={application.applicantCompany}
                                address={application.applicantAddress}
                                url={application.url || application.sourceUrl}
                            />
                        </Grid>
                    </Grid>
                    {/* To be added when geocoding is confirmed to be working properly */}
                    {/* <Grid container> */}
                    {/*    <Grid item xs={12} className={classes.grid}> */}
                    {/*        <Typography variant="caption"> */}
                    {/*            Geolocated */}
                    {/*        </Typography> */}
                    {/*    </Grid> */}
                    {/* </Grid> */}
                </AccordionDetails>
            </Accordion>
        );
    }
}

const style = theme => ({
    root: {
        '&:before':{
            height: 0
        },
        '&$expanded': {
            '&:not(:last-child)': {
                marginBottom: theme.constants.verticalMargin
            }
        },
        margin: 0,
        borderRadius: 5,
        border: `1px solid ${theme.palette.background.border}`,
        boxShadow: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
            border: `1px solid ${theme.palette.secondary.light}`,
            boxShadow: `0px 0px 0px 1px ${theme.palette.secondary.light} inset`
        }
    },
    expanded: {},
    summaryRoot:{
        minHeight: '0 !important',
        padding: 12
    },
    summaryContent: {
        alignItems: 'center',
        margin: '0 !important'
    },
    summaryContentDiv: {
        '&:last-child':{
            marginLeft: 'auto',
            textAlign: 'right'
        },
        '&:not(:last-child)': {
            marginLeft: 12
        },
        '&> *:not(:first-child)': {
            marginTop: 5
        }
    },
    detailsRoot: {
        flexDirection: 'column',
        padding: 0
    },
    gridLite: {
        padding: 12,
        borderTop: `1px solid ${theme.palette.background.border}`,
        '&:not(:last-child)': {
            borderRight: `1px solid ${theme.palette.background.border}`
        }
    },
    grid: {
        borderTop: `1px solid ${theme.palette.background.border}`,
        '&:not(:last-child)': {
            borderRight: `1px solid ${theme.palette.background.border}`
        },
        padding: 12,
        '&> *': {
            padding: 0,
            '&:not(:last-child)': {
                paddingBottom: 6
            },
            '&:not(:first-child)': {
                paddingTop: 6
            }
        }
    },
    heading: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    chip: {
        borderRadius: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3px 8px',
        lineHeight: 1
    },
    textWithIcon: {
        display: 'flex',
        alignItems: 'center',
        lineHeight: 1,
        '& > svg': {
            fontSize: '12px'
        }
    },
    granted: {
        backgroundColor: '#0217fa',
        color: 'white',
        textShadow: '1px 1px #00000080'
    },
    pending: {
        backgroundColor: '#6dd0f2',
        color: 'white',
        textShadow: '1px 1px #00000080'
    },
    refused: {
        backgroundColor: '#fdae61',
        color: 'white',
        textShadow: '1px 1px #00000080'
    },
    withdrawn: {
        backgroundColor: '#f7686a',
        color: 'white',
        textShadow: '1px 1px #00000080'
    },
    unknown: {
        backgroundColor: theme.palette.grey.variant3,
        color: 'white',
        textShadow: '1px 1px #00000080'
    },
    // ...chipStyles(theme)
});

export default withStyles(style)(PlanningCard);